// tableCell.tsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TableColumnConfig, SQLTableProps } from '../tableMain';
import EditableCell from './editableCell';
import { useApiData, EditResult } from '../../../contexts/apiService';
import { enqueueEdit, cancelQueue, EditCancelledError } from '../../../contexts/editQueue';
import { getModifierKey, getHoustonTime } from '../../../helper_functions';
import { PanelRight as PanelRightIcon, TriangleAlert, CircleAlert, EyeOff, Check, X, Info, Lock } from 'lucide-react';
import CalculationTooltip from './calculationTooltip';
import './tableCell.css';
import WarningBox from '../../../components/common/WarningBox'
import { USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import { submitFirebaseCellEdit } from './firebaseEdit';
import WarningMenu from '../../common/warning_menu/warningMenu';
import IconTooltip from '../../common/icon_tooltip/iconTooltip';
import { CellWarning } from '../dataProcessing';
import { trackEvent } from '../../../contexts/userAnalytics';

interface TableCellProps {
    tableType: SQLTableProps['tableType'];
    value: any;
    rowData: any;
    tableName: string;
    cellConfig: TableColumnConfig;
    isRowSelected: boolean;
    idValue: number;
    colName: string;
    actualColName?: string;
    cellWidth?: React.CSSProperties;
    isFirstRow?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    onTabToNextCell?: (direction?: number) => void;
    editingCell: { rowID: number; colName: string } | null;
    setEditingCell: React.Dispatch<React.SetStateAction<{ rowID: number; colName: string } | null>>;
    userRoleLevel?: number; // User role level for access control
    userData?: any; // User data for censored display
    // side panel props
    isPanelOpen?: boolean;
    onPanelToggle?: () => void;
    onPanelClose?: () => void;
    // warning parameters
    cellWarnings?: CellWarning[];
    // Rules that still match today but are suppressed via ignored_warnings — an
    // admin-only, read-only reconciliation indicator (only ever passed for the id cell).
    ignoredActiveWarnings?: CellWarning[];
}

const TableCell: React.FC<TableCellProps> = ({
    tableType,
    value,
    rowData,
    cellConfig,
    isRowSelected,
    cellWidth,
    isFirstRow,
    idValue,
    colName,
    actualColName,
    tableName,
    inputRef,
    onTabToNextCell,
    editingCell,
    setEditingCell,
    userRoleLevel = 1, // default to viewer,
    userData,
    // side panel props
    isPanelOpen = false,
    onPanelToggle,
    onPanelClose,
    cellWarnings,
    ignoredActiveWarnings,
}) => {

    const { handleEditOrder, handleAddRow, userDetails, fetchAllUsers, rowLocks, finishedJobs } = useApiData();
    const [isEditLoading, setIsEditLoading] = useState(false);
    // Waiting behind earlier edits to the same load (see editQueue)
    const [isQueued, setIsQueued] = useState(false);
    // Saved; the reprocessing job it triggered hasn't finished yet
    const [awaitingJobId, setAwaitingJobId] = useState<string | null>(null);
    const awaitingProcessing = !!awaitingJobId;
    // Queued edits run later, so they read the latest row and this cell's last saved value
    const rowDataRef = useRef(rowData);
    rowDataRef.current = rowData;
    const valueRef = useRef(value);
    valueRef.current = value;
    const lastSubmittedRef = useRef<{ value: any } | null>(null);

    // Edits to a load and its pickup/delivery/main_table rows share one queue and one lock
    const editQueueKey = `${tableName}:${tableName === 'schedule_table' ? rowData?.id : idValue}`;
    const rowLock = rowLocks[editQueueKey]
        || (tableName === 'schedule_table' && rowData?.main_table_id ? rowLocks[`main_table:${rowData.main_table_id}`] : undefined);
    const isLockedByOther = !!rowLock && rowLock.locked_by_uid !== userDetails?.id;
    const lockMessage = rowLock ? `Processing changes from ${rowLock.locked_by || 'another user'} - editing is locked until it finishes` : '';
    const [editValue, setEditValue] = useState(value);
    const [isFlashingError, setIsFlashingError] = useState(false);
    const [isFlashingSuccess, setIsFlashingSuccess] = useState(false);
    const [showStatusIcon, setShowStatusIcon] = useState<'success' | 'error' | null>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [showPermissionWarning, setShowPermissionWarning] = useState(false);
    // Which warning's popover (diff info and/or Resolve/Cancel) is open for this cell —
    // one shared state so at most one popover can ever be open per cell, regardless of
    // warning type.
    const [openWarningKey, setOpenWarningKey] = useState<string | null>(null);
    const [showResolveConfirm, setShowResolveConfirm] = useState(false);
    const [pendingResolveWarning, setPendingResolveWarning] = useState<CellWarning | null>(null);
    const [pendingEditData, setPendingEditData] = useState<{
        tableName: string;
        id: string;
        columnName: string;
        oldValue: string;
        newValue: string;
        ticketNumber: string;
        rid: string;
    } | null>(null);
    const [showTextTooltip, setShowTextTooltip] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
    const [tooltipCoords, setTooltipCoords] = useState<{ top: number; left: number } | null>(null);

    const warningIconRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const cellValueRef = useRef<HTMLDivElement>(null);
    const calcInfoIconRef = useRef<HTMLDivElement>(null);
    const [showCalcTooltip, setShowCalcTooltip] = useState(false);

    const isEditing = editingCell?.rowID === idValue && editingCell?.colName === colName && cellConfig.colType !== 'calculated';

    const sidePanelKeyValue = rowData?.[`${colName}_key`];
    const canEditSidePanelCell = sidePanelKeyValue !== 'multiple_values_key';

    // Keep editValue in sync with prop value
    React.useEffect(() => {
        setEditValue(value);
        lastSubmittedRef.current = null;
    }, [value]);

    React.useEffect(() => {
        const outcome = awaitingJobId ? finishedJobs[awaitingJobId] : undefined;
        if (!outcome) return;
        setAwaitingJobId(null);
        if (!outcome.success) {
            alert(`Your edit was saved, but reprocessing this load failed - ${outcome.error || 'unknown error'}. Re-run processing or contact support.`);
        }
    }, [awaitingJobId, finishedJobs]);

    const triggerErrorFlash = () => {
        setIsFlashingError(true);
        setShowStatusIcon('error');
        setTimeout(() => {
            setIsFlashingError(false);
            setShowStatusIcon(null);
        }, 1000); // Match the animation duration
    };

    const triggerSuccessFlash = () => {
        setIsFlashingSuccess(true);
        setShowStatusIcon('success');
        setTimeout(() => {
            setIsFlashingSuccess(false);
            setShowStatusIcon(null);
        }, 1000); // Match the animation duration
    };

    const handleCellClick = (e: React.MouseEvent) => {
        if (cellConfig.colType === 'calculated') return;
        if (isLockedByOther) return;

        // If modifier key is pressed, don't enter edit mode - let row selection happen
        if (getModifierKey(e)) {
            return; // Don't stopPropagation, let it bubble up to row selection
        }

        // Handle sidepanel toggle
        if (cellConfig.enableSidepanel && onPanelToggle) {
            if (!canEditSidePanelCell) {
                e.stopPropagation(); // Prevent row selection
                onPanelToggle();
                return; // Don't enter edit mode when opening panel
            }
        }

        // Only enter edit mode if no modifier key is pressed and no sidepanel
        const editableValue = typeof cellConfig.editable === 'function' ? cellConfig.editable(rowData) : cellConfig.editable;
        const isEditable = editableValue && canEditSidePanelCell;
        const userRole = Object.keys(USER_ROLE_LEVEL).find(key => USER_ROLE_LEVEL[key as keyof typeof USER_ROLE_LEVEL] === userRoleLevel) as keyof typeof USER_ROLE_LEVEL;
        const isRoleRestricted = cellConfig.rolesNotAllowedToEdit ? cellConfig.rolesNotAllowedToEdit?.includes(userRole) : false;
        const hasPermission = !isRoleRestricted && (!cellConfig.editableLevel || userRoleLevel >= cellConfig.editableLevel);
        if (isEditable && hasPermission) {
            e.stopPropagation(); // Allow row selection when clicking to edit
            setEditingCell({ rowID: idValue, colName });
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setEditValue(e.target.value);
    };

    const parseScheduleData = (data: any) => {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch (e) {
                console.error("Failed to parse data:", e);
                return [];
            }
        }
        return Array.isArray(data) ? data : [];
    };

    const finishEdit = async () => {
        if (cellConfig.colType === 'date' || cellConfig.colType === 'datetime') {
            // Check if the date is in YYYY-MM-DD format
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

            if (cellConfig.colType === 'date' && !dateRegex.test(editValue)) {
                console.log("Invalid date format. Must be YYYY-MM-DD");
                setEditValue(value); // Reset to original value
                setEditingCell(null);
                triggerErrorFlash();
                return;
            }

            // Parse the date and check if it's valid
            const inputDate = new Date(editValue);
            if (isNaN(inputDate.getTime())) {
                console.log("Invalid date");
                setEditValue(value); // Reset to original value
                setEditingCell(null);
                triggerErrorFlash();
                return;
            }

            // Check if the date is not more than 100 days from today
            const today = new Date();
            const daysDifference = Math.abs((inputDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            if (daysDifference > 100) {
                console.log("Date cannot be more than 100 days from today");
                setEditValue(value); // Reset to original value
                setEditingCell(null);
                triggerErrorFlash();
                return;
            }
        }

        setEditingCell(null);
        const previousStatusesToCheck = ['Completed', 'Canceled', 'Rejected'];
        const isStatusModified = (tableName === 'schedule_table' && colName === 'status' && previousStatusesToCheck.includes(rowData.status));
        const hasValueChange = value !== editValue;
        const needsApproval = cellConfig.requiresApproval && (!cellConfig.approvalLevel || userRoleLevel < cellConfig.approvalLevel);
        const currentStatus = rowData.status;

        const approvalTableName = cellConfig.subTable || tableName;

        let tableId = rowData.id;
        
        trackEvent('edit_cell', { tableName: tableName, subTable: cellConfig.subTable, colName: colName });

        if (cellConfig.subTable === 'main_table') {
            tableId = rowData.main_table_id;
        } else if (cellConfig.subTable === 'crude_schedule_pickup' && rowData.pickup_data) {
            try {
                const data = typeof rowData.pickup_data === 'string' ? JSON.parse(rowData.pickup_data) : rowData.pickup_data;
                const arr: any[] = Array.isArray(data) ? data : Object.values(data);
                const row = arr.find((r: any) => r?.sequence === 1);
                if (row?.id) tableId = row.id;
            } catch (error) {
                // Parsing failures are non-fatal here; we fall back to using `rowData.id` as `tableId`.
                // Log the error to aid in diagnosing bad `pickup_data` payloads.
                // eslint-disable-next-line no-console
                console.error('Failed to parse pickup_data JSON when deriving tableId', {
                    error,
                    rowId: rowData?.id,
                    pickup_data: rowData?.pickup_data,
                });
            }
        } else if (cellConfig.subTable === 'crude_schedule_delivery' && rowData.delivery_data) {
            try {
                const data = typeof rowData.delivery_data === 'string' ? JSON.parse(rowData.delivery_data) : rowData.delivery_data;
                const arr: any[] = Array.isArray(data) ? data : Object.values(data);
                const row = arr.find((r: any) => r?.sequence === 1);
                if (row?.id) tableId = row.id;
            } catch (error) {
                // Parsing failures are non-fatal here; we fall back to using `rowData.id` as `tableId`.
                // Log the error to aid in diagnosing bad `delivery_data` payloads.
                // eslint-disable-next-line no-console
                console.error('Failed to parse delivery_data JSON when deriving tableId', {
                    error,
                    rowId: rowData?.id,
                    delivery_data: rowData?.delivery_data,
                });
            }
        }

        
        if (actualColName) {
            colName = actualColName;
        }
        
        if (actualColName) {
            colName = actualColName;
        }
        
        if (currentStatus !== "Scheduled" && hasValueChange && (needsApproval || isStatusModified)) {
            if (cellConfig.subTable === 'crude_schedule_pickup' && rowData.pickup_data) {
                tableId = parseScheduleData(rowData.pickup_data)[0].id
            }

            if (cellConfig.subTable === 'crude_schedule_delivery' && rowData.delivery_data) {
                tableId = parseScheduleData(rowData.delivery_data)[0].id
            }

            setPendingEditData({
                tableName: approvalTableName,
                id: tableId,
                columnName: cellConfig.actualColName || colName,
                oldValue: value,
                newValue: editValue,
                ticketNumber: rowData.ticket_number, // Include ticket number for schedule_table status changes
                rid: rowData.rid, // Include rid for better tracking of pending changes
            });
            setShowPermissionWarning(true);

            return
        }

        if (editValue !== value) {
            const submittedValue = editValue;
            const submittedColName = colName;
            setIsQueued(true);
            try {
                await enqueueEdit(editQueueKey, async () => {
                    setIsQueued(false);
                    setIsEditLoading(true);
                    // Read when the edit runs, not when it was queued: an earlier queued edit
                    // of this cell may have changed the value since (ticket_assignment unlinks
                    // whatever old_value names)
                    const oldValue = lastSubmittedRef.current ? lastSubmittedRef.current.value : valueRef.current;
                    try {
                        await submitEdit(oldValue, submittedValue, submittedColName, value);
                    } finally {
                        setIsEditLoading(false);
                    }
                });
            } catch (error) {
                setIsQueued(false);
                if (error instanceof EditCancelledError) {
                    setEditValue(value);
                    triggerErrorFlash();
                }
            }
        }
    };

    // Shared success/failure handling for every edit path.
    const handleEditResult = (success: boolean, response: any, result: EditResult | undefined, originalValue: any, submittedValue: any) => {
        if (success) {
            lastSubmittedRef.current = { value: submittedValue };
            if (result?.processing && result.job_id) setAwaitingJobId(result.job_id);
            triggerSuccessFlash();
            return;
        }

        console.error(`edit failed`);
        setEditValue(originalValue); // Reset to original value on failure
        triggerErrorFlash();

        if (result?.status === 423) {
            // Anything else queued for this load would be rejected the same way
            cancelQueue(editQueueKey);
            alert(`${result.locked_by || 'Another user'} is editing this load. Your change was not saved - try again once it unlocks.`);
        } else if (result?.status === 409) {
            alert('This value was changed by someone else after you loaded it. Your change was not saved - refresh to see the current value.');
        } else if (response) {
            alert(`Failed to submit edit - ${response}`);
        }
    };

    // Sends one edit. Runs inside the load's edit queue: `value` is the cell's value when
    // the edit runs (sent as old_value), `editValue` the value submitted, and `revertValue`
    // what the cell showed when the edit was made, restored if it fails.
    const submitEdit = async (value: any, editValue: any, colName: string, revertValue: any) => {
            if (value === null || value === undefined) {
                value = '';
            }
            const originalValue = revertValue;
            // latest row, not the render that queued this edit (e.g. a pickup row an earlier
            // queued edit just created)
            const rowData = rowDataRef.current;
            
            // Subtable editing logic
            if (cellConfig.subTable !== null && cellConfig.subTable !== undefined) {
                if (cellConfig.subTable === 'crude_schedule_pickup') {
                    const findIdBySequence = (data: any, sequenceNumber: number): number | undefined => {
                        let dataArray: any[] = [];

                        // Handle JSON string
                        if (typeof data === 'string') {
                            try {
                                data = JSON.parse(data);
                            } catch (e) {
                                console.error("Failed to parse JSON:", e);
                                return undefined;
                            }
                        }

                        // Convert to array
                        if (Array.isArray(data)) {
                            dataArray = data;
                        } else if (data && typeof data === 'object') {
                            dataArray = Object.values(data);
                        } else {
                            return undefined;
                        }

                        const foundRow = dataArray.find(row =>
                            row && typeof row === 'object' && row.sequence === sequenceNumber
                        );
                        return foundRow?.id;
                    };

                    const idForSequence1 = findIdBySequence(rowData.pickup_data, 1);
                    if (!idForSequence1) {
                        // If no ID is found, we may need to add a new row
                        const newRow = {
                            schedule_main_id: rowData.id,
                            sequence: 1,
                            [colName]: editValue,
                        };
                        await handleAddRow('crude_schedule_pickup', newRow);
                        setEditValue(originalValue); // Reset to original value
                        triggerErrorFlash();
                        return;
                    } else {
                        const colName = cellConfig.actualColName || cellConfig.colName;

                        const [success, response, result] = await handleEditOrder('crude_schedule_pickup', idForSequence1, colName, value, editValue, rowData.id);
                        handleEditResult(success, response, result, originalValue, editValue);
                    }
                    return;

                }
                if (cellConfig.subTable === 'crude_schedule_delivery') {
                    const findIdBySequence = (data: any, sequenceNumber: number): number | undefined => {
                        let dataArray: any[] = [];

                        // Handle JSON string
                        if (typeof data === 'string') {
                            try {
                                data = JSON.parse(data);
                            } catch (e) {
                                console.error("Failed to parse JSON:", e);
                                return undefined;
                            }
                        }

                        // Convert to array
                        if (Array.isArray(data)) {
                            dataArray = data;
                        } else if (data && typeof data === 'object') {
                            dataArray = Object.values(data);
                        } else {
                            return undefined;
                        }

                        const foundRow = dataArray.find(row =>
                            row && typeof row === 'object' && row.sequence === sequenceNumber
                        );
                        return foundRow?.id;
                    };

                    const idForSequence1 = findIdBySequence(rowData.delivery_data, 1);

                    if (!idForSequence1) {
                        // If no ID is found, we may need to add a new row
                        const newRow = {
                            schedule_main_id: rowData.id,
                            sequence: 1,
                            destination_id: editValue,
                        };

                        await handleAddRow('crude_schedule_delivery', newRow);

                        setEditValue(originalValue); // Reset to original value
                        triggerErrorFlash();
                        return;
                    } else {
                        // if ID is found for sequence = 1, edit that row
                        const colName = cellConfig.actualColName || cellConfig.colName;

                        const [success, response, result] = await handleEditOrder('crude_schedule_delivery', idForSequence1, colName, value, editValue, rowData.id);
                        handleEditResult(success, response, result, originalValue, editValue);
                        return;
                    }
                } if (cellConfig.subTable === 'main_table') {
                    const [success, response, result] = await handleEditOrder(cellConfig.subTable, rowData.main_table_id, colName, value, editValue, rowData.id);
                    handleEditResult(success, response, result, originalValue, editValue);
                    return;

                } else {
                    const [success, response, result] = await handleEditOrder(cellConfig.subTable, rowData.main_table_id, colName, value, editValue, rowData.id);
                    handleEditResult(success, response, result, originalValue, editValue);
                    return;
                }
            }

            let success;
            let response;
            let result: EditResult | undefined;

            if (tableType === 'firebase') {
                [success, response] = await submitFirebaseCellEdit({
                    collectionName: tableName,
                    docId: String(idValue),
                    fieldName: cellConfig.actualColName || colName,
                    oldValue: value,
                    newValue: editValue,
                    colType: cellConfig.colType,
                });
                fetchAllUsers(); // Refresh users after update
            } else {
                // The schedule-table carrier-cost persist hook (web_edit_row.py) trusts a
                // client-supplied candidate set rather than re-deriving the product/basin
                // eligibility filter server-side — reuse the exact same getFilteredOptions
                // call the dropdown itself used to render, so the two never drift.
                let extra: Record<string, any> | undefined;
                if (tableName === 'schedule_table' && colName === 'carrier_id' && cellConfig.getFilteredOptions && cellConfig.dependsOn) {
                    const dependentValue = rowData[cellConfig.dependsOn];
                    // Omit eligible_carrier_ids entirely when there's no dependent value to
                    // filter on (e.g. product not set yet) — sending an empty list here would
                    // read as "the eligible set really is empty", not "we couldn't compute one".
                    // (The backend also guards against a degenerate candidate set on its own —
                    // see the len(valid) > 1 check in web_edit_row.py — this is belt-and-suspenders.)
                    if (dependentValue) {
                        const eligible = cellConfig.getFilteredOptions(dependentValue, cellConfig.dropdownOptions || [], rowData);
                        extra = { eligible_carrier_ids: eligible.map((opt: any) => opt.value) };
                    }
                }
                // Stale-write guard: the raw DB value this edit was based on. Only sent for the
                // row's own columns, where rowData holds exactly that column (sub-table cells
                // show joined/display values). A queued earlier edit of this cell supersedes it.
                const latestRowData = rowDataRef.current;
                if (colName in latestRowData) {
                    const expected = lastSubmittedRef.current ? lastSubmittedRef.current.value : latestRowData[colName];
                    extra = { ...(extra || {}), expected_value: expected === '' ? null : expected };
                }
                [success, response, result] = await handleEditOrder(tableName, idValue, colName, value, editValue, undefined, undefined, extra);
            }

            handleEditResult(!!success, response, result, originalValue, editValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (e.key === 'Enter') {
            finishEdit();
        } else if (e.key === 'Escape') {
            setEditingCell(null);
            setEditValue(value);
        }
    };

    // Helper function to format accounting values
    const formatAccountingValue = (val: any): { dollarSign: string; amount: string; isNegative: boolean; redNegative: boolean } => {
        if (val === null || val === undefined || val === '') return { dollarSign: '', amount: '', isNegative: false, redNegative: false };

        const numValue = typeof val === 'string' ? parseFloat(val) : Number(val);
        if (isNaN(numValue)) return { dollarSign: '', amount: String(val), isNegative: false, redNegative: false };

        const decimals = cellConfig.decimals ?? 2; // Default to 2 decimal places
        const redNegative = cellConfig.redNegative ?? false;
        const isNegative = numValue < 0;
        const absValue = Math.abs(numValue);

        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(absValue);

        // Remove the "$" and get just the number part
        const amount = formatted.substring(1); // Remove "$"

        if (isNegative) {
            return { dollarSign: '$', amount: `(${amount})`, isNegative: true, redNegative };
        } else {
            return { dollarSign: '$', amount, isNegative: false, redNegative };
        }
    };

    const formatWithDecimals = (val: any, decimals: number = 2): string => {
        if (val === null || val === undefined || val === '') return '';

        const numValue = typeof val === 'string' ? parseFloat(val) : Number(val);
        if (isNaN(numValue)) return String(val);

        // Check if the original value was an integer
        const isInteger = Number.isInteger(numValue);

        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: isInteger ? 0 : decimals,
            maximumFractionDigits: decimals,
        }).format(numValue);
    };

    // Helper function to get display value
    const displayValue = useMemo((): React.ReactNode => {
        // Apply censored display if defined (do this first, before null checks)
        const effectiveValue = cellConfig.conditionalDisplay 
            ? cellConfig.conditionalDisplay(value, rowData, userData)
            : value;
        
        
        // Now check if the effective value (after censoring) is empty
        if (effectiveValue === null || effectiveValue === undefined || effectiveValue === '') return '';

        if (typeof effectiveValue === 'string' && isNaN(Number(effectiveValue)) && cellConfig.conditionalDisplay && (cellConfig.conditionalDisplay(value, rowData, userData) !== value)) {
            return effectiveValue;
        }

        if (cellConfig.colType === 'calculated') {
            if (typeof effectiveValue === 'string' && isNaN(Number(effectiveValue))) {
                return String(effectiveValue);
            }
            const vt = cellConfig.calculationConfig?.valueType ?? 'number';
            if (vt === 'accounting') {
                const { dollarSign, amount, isNegative, redNegative } = formatAccountingValue(effectiveValue);
                if (!dollarSign && !amount) return '';
                return (
                    <div className="tableCell-cellValue--accounting">
                        <span className={`tableCell-dollarsign ${isNegative && redNegative ? 'tableCell-dollarsign--negative' : ''}`}>
                            {dollarSign}
                        </span>
                        <span className={`tableCell-amount ${isNegative && redNegative ? 'tableCell-amount--negative' : ''}`}>
                            {amount}
                        </span>
                    </div>
                );
            }
            const decimals = cellConfig.decimals ?? 2;
            return formatWithDecimals(effectiveValue, decimals);
        }

        if (cellConfig.colType === 'accounting') {
            // If effectiveValue is a string (censored), display it as-is
            if (typeof effectiveValue === 'string' && isNaN(Number(effectiveValue))) {
                return String(effectiveValue);
            }
            
            const { dollarSign, amount, isNegative, redNegative } = formatAccountingValue(effectiveValue);
            if (!dollarSign && !amount) return '';

            return (
                <div className="tableCell-cellValue--accounting">
                    <span className={`tableCell-dollarsign ${isNegative && redNegative ? 'tableCell-dollarsign--negative' : ''}`}>
                        {dollarSign}
                    </span>
                    <span className={`tableCell-amount ${isNegative && redNegative ? 'tableCell-amount--negative' : ''}`}>
                        {amount}
                    </span>
                </div>
            );
        }
            
        if (cellConfig.colType === 'number') {
            // If effectiveValue is a string (censored), display it as-is
            if (typeof effectiveValue === 'string' && isNaN(Number(effectiveValue))) {
                return effectiveValue;
            }
            
            const decimals = cellConfig.decimals ?? 2;
            return formatWithDecimals(effectiveValue, decimals);
        }

        if (cellConfig.colType === 'boolean') {
            let displayValue = '';

            if (effectiveValue === 1 || effectiveValue === true || effectiveValue === 'true') {
                displayValue = 'Yes';
            } else if (effectiveValue === 0 && cellConfig.noAsNull === true || effectiveValue === 'false' && cellConfig.noAsNull === true || effectiveValue === false && cellConfig.noAsNull === true) {
                displayValue = '';
            } else {
                displayValue = 'No';
            }

            return displayValue;
        }
        
        if (cellConfig.colType === 'month') {
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
            const num = typeof effectiveValue === 'number' ? effectiveValue : parseInt(String(effectiveValue), 10);
            if (!isNaN(num) && num >= 1 && num <= 12) {
                return monthNames[num - 1];
            }
            return String(effectiveValue);
        }

        if (
            tableName === 'schedule_table' &&
            colName === 'lease_name' &&
            typeof effectiveValue === 'string' &&
            effectiveValue.includes(' -> ')
        ) {
            const [first, ...rest] = effectiveValue.split(' -> ');
            return (
                <>
                    <span style={{ textDecoration: 'line-through' }}>{first}</span>
                    {' -> '}
                    {rest.join(' -> ')}
                </>
            );
        }

        if (tableType === 'firebase' && cellConfig.enableSidepanel && colName === 'dataAccess') {
            const isTruthyAccessValue = (v: any): boolean => {
                if (v === true || v === 1 || v === '1' || v === 'true') return true;
                if (v && typeof v === 'object' && v.enabled === true) return true;
                return false;
            };

            const countAssignments = (source: any): number => {
                if (!source) return 0;
                if (Array.isArray(source)) {
                    return source.filter(Boolean).length;
                }
                if (typeof source === 'object') {
                    return Object.keys(source).filter((k) => isTruthyAccessValue(source[k])).length;
                }
                return 0;
            };

            const dataAccess = effectiveValue || {};
            const entityCount = countAssignments(dataAccess.entity_id);
            const groupCount = countAssignments(dataAccess.group_id);
            const total = entityCount + groupCount;

            return total > 0 ? `${total} Assignments` : 'No access';
        }

        return String(effectiveValue);
    }, [value, cellConfig.colType, cellConfig.decimals, cellConfig.redNegative, cellConfig.conditionalDisplay, cellConfig.noAsNull, cellConfig.calculationConfig?.valueType, rowData, userData]);

    useEffect(() => {
        if (isEditing && inputRef?.current) {
            inputRef.current.focus();
        }
    }, [isEditing, inputRef]);

    // Check if text is truncated to show tooltip
    useEffect(() => {
        const element = cellValueRef.current;
        if (element && !isEditing) {
            const isTruncated = element.scrollWidth > element.clientWidth;
            setIsTruncated(isTruncated);
        }
    }, [displayValue, isEditing]);

    const handleConfirmEditRequest = (reason?: string) => {

        if (pendingEditData && userDetails) {
            const rowData = {
                table_name: pendingEditData.tableName,
                action: 'edit',
                request_user: userDetails.name,
                request_user_role: userDetails.role,
                request_user_id: userDetails.id,
                edited_col: pendingEditData.columnName,
                new_value: pendingEditData.newValue,
                requested_on: getHoustonTime(),
                old_value: pendingEditData.oldValue,
                request_reason: reason || '',
                status: 'Pending',
                pending_row_id: pendingEditData.id,
                pdf_ticket_number: pendingEditData.ticketNumber, // Include ticket number for schedule_table status changes
                rid: pendingEditData.rid,
            };
            
            console.log("Submitting edit request:", rowData);
            setEditValue(pendingEditData.newValue);
            handleAddRow('pending_approvals', rowData);
            setShowPermissionWarning(false);
            setEditingCell(null);
        }
    };

    const handleCancelEditRequest = () => {
        setShowPermissionWarning(false);
        if (pendingEditData) {
            setEditValue(pendingEditData.oldValue);
        }
    }

    const handleConfirmResolveWarning = async (reason?: string) => {
        if (!pendingResolveWarning || !userDetails) return;
        if (rowData?.id == null) { console.error('Cannot resolve warning: missing rowData.id'); triggerErrorFlash(); return; }
        const resolveRowData = {
            action: 'resolve_warning',
            table_name: tableName,
            request_user: userDetails.name,
            request_user_role: userDetails.role,
            request_user_id: userDetails.id,
            edited_col: pendingResolveWarning.key,
            new_value: pendingResolveWarning.key,
            requested_on: getHoustonTime(),
            old_value: null,
            request_reason: reason || '',
            status: 'Pending',
            // Always the row's real numeric PK, not idValue (rowData[idColumn]) — most
            // tables key on `id` already, but vac_accounts_payable's idColumn is `rid`
            // (a string) while warning_adjustment.row_id is the numeric id the backend
            // JOIN expects.
            pending_row_id: rowData.id,
            rid: rowData?.rid,
        };

        const success = await handleAddRow('pending_approvals', resolveRowData);
        if (success) {
            setShowResolveConfirm(false);
            setPendingResolveWarning(null);
            triggerSuccessFlash();
        } else {
            // Keep the confirm dialog open so the user can retry instead of silently losing the request.
            triggerErrorFlash();
        }
    };

    const handleCancelResolveWarning = () => {
        setShowResolveConfirm(false);
        setPendingResolveWarning(null);
    };

    // Decide cell-specific background color from config rules
    const cellBackgroundColor = React.useMemo(() => {
        const rules = cellConfig?.cellColor || [];
        for (const rule of rules) {
            try {
                if (typeof rule?.condition === 'function' && rule.condition(rowData)) {
                    return rule.color;
                }
            } catch (e) {
                // Guard against bad condition functions
                console.warn('cellColor.condition threw an error:', e);
            }
        }
        return undefined;
    }, [cellConfig.cellColor, rowData]);

    return (
        <>
            <div
                className={
                    `tableCell-cellContainer${isEditLoading ? ' tableCell-cellContainer--loading' : ''
                    } ${isQueued ? ' tableCell-cellContainer--queued' : ''
                    } ${awaitingProcessing && !isEditLoading ? ' tableCell-cellContainer--processing' : ''
                    } ${isLockedByOther ? ' tableCell-cellContainer--locked' : ''
                    } ${isFlashingError ? ' tableCell-cellContainer--error-flash' : ''
                    } ${isFlashingSuccess ? ' tableCell-cellContainer--success-flash' : ''
                    } ${(!canEditSidePanelCell && cellConfig.enableSidepanel) ? ' tableCell-sidePanelCell' : ''
                    } ${isPanelOpen ? ' tableCell-panelOpen' : ''
                    } ${isRowSelected ? 'selected' : ''
                    }`
                }
                style={{
                    ...cellWidth,
                    ...(isFirstRow && { borderTop: '1.5px solid var(--color-grey-border-light)' }),
                    ...((cellConfig.colType === 'calculated' || !(typeof cellConfig.editable === 'function' ? cellConfig.editable(rowData) : cellConfig.editable) || (!canEditSidePanelCell && !cellConfig.enableSidepanel)) && { color: 'var(--color-grey)' }),
                    ...(cellBackgroundColor ? { backgroundColor: cellBackgroundColor } : {})
                }}
                onClick={handleCellClick}
                title={isLockedByOther ? lockMessage : isQueued ? 'Queued - waits for earlier edits to this load' : awaitingProcessing ? 'Saved - reprocessing this load' : undefined}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {isEditing ? (
                    <EditableCell
                        value={editValue}
                        displayValue={displayValue as string}
                        rowData={rowData}
                        cellConfig={cellConfig}
                        inputRef={inputRef}
                        onChange={handleInputChange}
                        onBlur={finishEdit}
                        onKeyDown={(e) => {
                            if (e.key === 'Tab') {
                                e.preventDefault();
                                finishEdit();
                                if (onTabToNextCell) {
                                    if (e.shiftKey) {
                                        onTabToNextCell(-1); // Move to previous cell
                                    } else {
                                        onTabToNextCell(1); // Move to next cell
                                    }
                                }
                            } else {
                                handleKeyDown(e);
                            }
                        }}
                        isDisabled={isEditLoading}
                    />
                ) : (
                    cellConfig.colType === 'accounting' ? (
                        displayValue
                    ) : cellConfig.colType === 'calculated' ? (
                        <div className="tableCell-rowContainer tableCell-rowContainer--calculated">
                            <div className="tableCell-cellValue" ref={cellValueRef}>
                                {displayValue}
                            </div>
                            {cellConfig.calculationConfig && (
                                <>
                                    <div
                                        ref={calcInfoIconRef}
                                        className={`tableCell-calcInfoIcon${showCalcTooltip ? ' tableCell-calcInfoIcon--active' : ''}`}
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setShowCalcTooltip(prev => !prev);
                                        }}
                                    >
                                        <Info size={14} style={{ color: 'var(--color-grey-lighter)' }} />
                                    </div>
                                    {showCalcTooltip && createPortal(
                                        <CalculationTooltip
                                            isOpen={showCalcTooltip}
                                            onClose={() => setShowCalcTooltip(false)}
                                            anchorRef={calcInfoIconRef as React.RefObject<HTMLElement>}
                                            formula={cellConfig.calculationConfig.formula}
                                            rowData={rowData}
                                            labels={cellConfig.calculationConfig.labels}
                                            valueType={cellConfig.calculationConfig.valueType ?? 'number'}
                                        />,
                                        document.body
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div
                            className="tableCell-rowContainer"
                            onMouseEnter={() => {
                                if (isTruncated) {
                                    // Calculate if there's enough space above for the tooltip
                                    const element = cellValueRef.current;
                                    if (element) {
                                        const rect = element.getBoundingClientRect();
                                        const spaceAbove = rect.top;
                                        const spaceBelow = window.innerHeight - rect.bottom;
                                        const position = spaceAbove < 100 ? 'bottom' : 'top';
                                        setTooltipPosition(position);
                                        
                                        // Calculate absolute position for portal
                                        const coords = {
                                            left: rect.left,
                                            top: position === 'top' ? rect.top : rect.bottom
                                        };
                                        setTooltipCoords(coords);
                                    }
                                    setShowTextTooltip(true);
                                }
                            }}
                            onMouseLeave={() => setShowTextTooltip(false)}
                        >
                            <div 
                                className="tableCell-cellValue" 
                                ref={cellValueRef}
                            >
                                {displayValue}
                            </div>
                            {rowLock && colName === 'id' && (
                                <div className="tableCell-lockIcon" title={lockMessage}>
                                    <Lock size={12} />
                                </div>
                            )}
                            {cellWarnings && cellWarnings.map((warning) => {
                                // Purely a visual choice (icon color/diff badge) — no longer decides
                                // which popover opens; every warning type now supports Resolve/Cancel.
                                const isCalculatedValueAlert = warning.type === 'alert' && warning.data?.calculatedValue !== undefined;
                                const setIconRef = (el: HTMLDivElement | null) => {
                                    if (el) warningIconRefs.current.set(warning.key, el);
                                    else warningIconRefs.current.delete(warning.key);
                                };
                                const toggleOpen = (e: React.MouseEvent) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpenWarningKey(openWarningKey === warning.key ? null : warning.key);
                                };
                                return (
                                    <React.Fragment key={warning.key || warning.description}>
                                        <div className="tableCell-warningIcon">
                                            {warning.type === 'alert' ? (
                                                <div
                                                    ref={setIconRef}
                                                    className="tableCell-cellIconAlert"
                                                    style={openWarningKey === warning.key ? { backgroundColor: 'white' } : undefined}
                                                    onClick={toggleOpen}
                                                >
                                                    <CircleAlert
                                                        size={16}
                                                        style={{ color: 'var(--color-warning-red)' }}
                                                    />
                                                    {isCalculatedValueAlert ? (
                                                        ((diff) => diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2))((warning.data.reportedValue || 0) - warning.data.calculatedValue)
                                                    ): null}
                                                </div>
                                            ) : (
                                                <div
                                                    ref={setIconRef}
                                                    className="tableCell-cellIcon"
                                                    onClick={toggleOpen}
                                                >
                                                    <TriangleAlert
                                                        size={16}
                                                        style={{ color: 'var(--color-warning-yellow)' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {openWarningKey === warning.key && createPortal(
                                            <WarningMenu
                                                isOpen={openWarningKey === warning.key}
                                                onClose={() => setOpenWarningKey(null)}
                                                onResolve={() => {
                                                    setOpenWarningKey(null);
                                                    setPendingResolveWarning(warning);
                                                    setShowResolveConfirm(true);
                                                }}
                                                description={warning.description}
                                                diffData={isCalculatedValueAlert ? warning.data : undefined}
                                                anchorEl={warningIconRefs.current.get(warning.key) ?? null}
                                            />,
                                            document.body
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {ignoredActiveWarnings && ignoredActiveWarnings.length > 0 && (
                                <div className="tableCell-ignoredWarningIcon">
                                    <IconTooltip
                                        icon={EyeOff}
                                        showTooltip={true}
                                        position="top"
                                        iconSize={16}
                                        iconColor="var(--color-grey-dark)"
                                        label={`Suppressed: ${ignoredActiveWarnings.map(w => w.description).join('; ')}`}
                                    />
                                </div>
                            )}
                        </div>
                    )
                )}

                {showStatusIcon && (
                    <div className="tableCell-statusIcon">
                        {showStatusIcon === 'success' ? (
                            <Check
                                size={24}
                                strokeWidth={3}
                                style={{ color: 'white' }}
                            />
                        ) : (
                            <X
                                size={24}
                                strokeWidth={3}
                                style={{ color: 'white' }}
                            />
                        )}
                    </div>
                )}
                {isHovered && cellConfig.enableSidepanel && !showStatusIcon && (
                    <div
                        className={
                            `tableCell-sidePanelIcon${isPanelOpen ? ' tableCell-sidePanelIcon-selected' : ''
                            }`
                        }
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onPanelToggle?.();
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onMouseUp={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <PanelRightIcon
                            size={20}
                            style={{color: 'var(--color-grey)', pointerEvents: 'none'}}
                        />
                    </div>
                )}
            </div>

            {isTruncated && showTextTooltip && tooltipCoords && createPortal(
                <div 
                    className={`tableCell-textTooltip tableCell-textTooltip--${tooltipPosition}`}
                    style={{
                        position: 'fixed',
                        left: `${tooltipCoords.left}px`,
                        top: tooltipPosition === 'top' 
                            ? `${tooltipCoords.top - 8}px`
                            : `${tooltipCoords.top + 8}px`,
                        transform: tooltipPosition === 'top' ? 'translateY(-100%)' : 'none'
                    }}
                >
                    {displayValue}
                </div>,
                document.body
            )}
            
            {showPermissionWarning && createPortal(
                <WarningBox
                    isOpen={showPermissionWarning}
                    onClose={handleCancelEditRequest}
                    onConfirm={handleConfirmEditRequest}
                    title="Edit Request"
                    message={`The request will be moved to approvals, please indicate reason for edit.`}
                    button1="Cancel"
                    button2="Submit Request"
                    showbutton2={true}
                    showTextInput={true}
                    textPlaceholder='Type reason for change...'
                    button2Color='green'
                />,
                document.body
            )}

            {showResolveConfirm && createPortal(
                <WarningBox
                    isOpen={showResolveConfirm}
                    onClose={handleCancelResolveWarning}
                    onConfirm={handleConfirmResolveWarning}
                    title="Resolve Warning"
                    message={`Resolve "${pendingResolveWarning?.description}" for this row? The request will be moved to approvals.`}
                    button1="Cancel"
                    button2="Submit Request"
                    showbutton2={true}
                    showTextInput={true}
                    textPlaceholder='Type reason for resolving (optional)...'
                    button2Color='green'
                />,
                document.body
            )}
        </>
    );
};

export default TableCell;
